import { ENDPOINTS } from '@constants'
import { AxiosResponse } from 'axios'
import { BaseApi } from './_base.api'

/**
 * KVK Company data structure returned by backend
 * This is the normalized format from the Laravel backend
 */
export interface IKvkCompany {
  kvk_number: string | null
  name: string | null
  trade_names: string[]
  street: string | null
  house_number: string | null
  house_number_addition: string | null
  /** Additional address specification (from detail endpoint only, e.g. floor/room) */
  toevoeging_adres: string | null
  /** Raw country field from detail endpoint */
  land: string | null
  postal_code: string | null
  city: string | null
  country: string | null
  type: string | null
}

/**
 * Backend search response format
 */
export interface IKvkSearchResponse {
  data: IKvkCompany[]
  total: number
}

/**
 * Backend company details response format
 */
export interface IKvkCompanyResponse {
  data: IKvkCompany
}

/**
 * Search query parameters for backend proxy
 */
export interface IKvkSearchQueryParams {
  query: string
  results_per_page?: number
}

/**
 * KVK API Client
 * Uses Laravel backend proxy for security (API key is hidden)
 *
 * Backend endpoints:
 * - GET /api/kvk/search?query=<search>&results_per_page=<number>
 * - GET /api/kvk/{kvkNumber}
 *
 * The backend handles:
 * - API authentication with KVK
 * - Rate limiting
 * - Caching (1 hour default)
 * - Data transformation to standardized format
 * - Error handling and logging
 */
export class KvkApi extends BaseApi {
  /**
   * Search for companies by name or KVK number
   * Uses backend proxy which caches results for 1 hour
   *
   * @param params Search parameters
   * @param params.query Company name or KVK number (min 2 characters)
   * @param params.results_per_page Number of results (default: 10, max: 100)
   * @returns Promise with search results and total count
   *
   * @example
   * ```typescript
   * const results = await kvkApi.searchCompany({
   *   query: 'Amsterdam',
   *   results_per_page: 10
   * })
   * ```
   */
  searchCompany = (params: IKvkSearchQueryParams) =>
    this.api
      .get(`${ENDPOINTS?.KVK || '/kvk'}/search`, {
        params,
      })
      .then((res: AxiosResponse<IKvkSearchResponse>) => res.data)

  /**
   * Get detailed company information by KVK number
   * Uses backend proxy which caches results for 1 hour
   *
   * @param kvkNumber 8-digit KVK number
   * @returns Promise with company details or null if not found
   *
   * @example
   * ```typescript
   * const company = await kvkApi.getCompanyByKvkNumber('12345678')
   * ```
   */
  getCompanyByKvkNumber = (kvkNumber: string) =>
    this.api.get(`${ENDPOINTS?.KVK || '/kvk'}/${kvkNumber}`).then((res: AxiosResponse<IKvkCompanyResponse>) => res.data)
}
